import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { LanguageSelector } from "@/components/LanguageSelector";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Phone, PhoneCall, Mic, Shield, Zap, Clock, TrendingUp, CheckCircle2, Code, Globe, Lock, BarChart, Headphones, Building2, Calendar, Facebook, Twitter, Linkedin, Instagram, Youtube, MapPin, MessageSquare, LayoutTemplate, Workflow, Radio, Menu, Webhook, Waves, PhoneIncoming, FileText, Stethoscope, Home, Utensils, Car, Scale, ShoppingCart, ArrowRight, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import TechPartners from "@/components/TechPartners";
import TestimonialsCarousel from "@/components/TestimonialsCarousel";
import PricingSection from "@/components/PricingSection";
import ChatInterface from "@/components/onboarding/ChatInterface";
import TemplateSelector from "@/components/onboarding/TemplateSelector";
import UrlAnalyzer from "@/components/onboarding/UrlAnalyzer";
import TestOptions from "@/components/onboarding/TestOptions";
import RegistrationPrompt from "@/components/onboarding/RegistrationPrompt";
import logo from "@/assets/logo.jpg";
import { ContactForm } from "@/components/ContactForm";
import { CookieConsent } from "@/components/tracking/CookieConsent";
import CallRecordingDemo from "@/components/CallRecordingDemo";
import LiveActivityTicker from "@/components/LiveActivityTicker";
import RotatingNewsBanner from "@/components/RotatingNewsBanner";
import SocialShareButtons from "@/components/SocialShareButtons";
import FeaturedBlogPosts from "@/components/FeaturedBlogPosts";
import { CalendlyModal } from "@/components/CalendlyModal";
type CreationMethod = "chat" | "url" | "template" | null;
type FlowStep = "home" | "test" | "register";
const Index = () => {
  const {
    t,
    i18n
  } = useTranslation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [activeMethod, setActiveMethod] = useState<CreationMethod>(null);
  const [flowStep, setFlowStep] = useState<FlowStep>("home");
  const [agentConfig, setAgentConfig] = useState<any>(null);
  const [sessionId, setSessionId] = useState<string>("");
  const [agentId, setAgentId] = useState<string>("");
  const [selectedFeature, setSelectedFeature] = useState<number | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [calendlyOpen, setCalendlyOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    // Handle hash navigation on page load and hash changes
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1);
      if (hash) {
        setTimeout(() => {
          const element = document.getElementById(hash);
          if (element) {
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }, 100);
      }
    };

    // Handle initial hash on load
    handleHashChange();

    // Listen for hash changes
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);
  useEffect(() => {
    if (activeMethod) {
      setTimeout(() => {
        const element = document.querySelector("[data-active-method]");
        if (element) {
          const elementTop = element.getBoundingClientRect().top + window.scrollY;
          const offset = 100; // Offset to account for header
          window.scrollTo({
            top: elementTop - offset,
            behavior: "smooth"
          });
        }
      }, 100);
    }
  }, [activeMethod]);
  const handleSmoothScroll = (sectionId: string) => {
    // Update URL hash for shareable links
    window.history.pushState(null, "", `#${sectionId}`);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth"
      });
    }
  };
  return <div className="min-h-screen bg-background">
      <Helmet>
        <html lang={i18n.language || 'en'} />
        <title>{t('seo.home.title')}</title>
        <meta name="description" content={t('seo.home.description')} />
        <meta name="keywords" content="24/7 AI call center, AI answering service, AI call center software, AI receptionist for small business, AI customer support platform, automate customer calls with AI, AI phone agent, virtual receptionist, call 24 answering service" />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Call24x7.AI" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://call24x7.ai/" />
        <meta property="og:title" content={t('seo.home.title')} />
        <meta property="og:description" content={t('seo.home.description')} />
        <meta property="og:image" content="https://call24x7.ai/og-image.png" />
        <meta property="og:site_name" content="Call24x7.AI" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://call24x7.ai/" />
        <meta name="twitter:title" content={t('seo.home.title')} />
        <meta name="twitter:description" content={t('seo.home.description')} />
        <meta name="twitter:image" content="https://call24x7.ai/og-image.png" />

        <link rel="canonical" href="https://call24x7.ai/" />

        {/* Structured Data - Organization */}
        <script type="application/ld+json">
          {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Call24x7.AI",
          alternateName: "Call24x7",
          description: "AI customer support platform providing 24/7 AI answering service, AI call center software, and AI receptionist service",
          url: "https://call24x7.ai",
          logo: "https://call24x7.ai/logo.png",
          contactPoint: {
            "@type": "ContactPoint",
            contactType: "Customer Service",
            email: "support@call24x7.ai",
            availableLanguage: ["English", "Spanish", "French", "German", "Hebrew", "Arabic"]
          },
          sameAs: ["https://www.facebook.com/p/Call24x7AI-61567709152635/", "https://www.instagram.com/call24x7.ai/", "https://www.youtube.com/channel/UCLLWm2gsbHx7a_rt5oLSGkA"]
        })}
        </script>

        {/* Structured Data - SoftwareApplication */}
        <script type="application/ld+json">
          {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "Call24x7.AI - AI Customer Support Platform",
          applicationCategory: "BusinessApplication",
          description: "AI customer support platform with 24/7 AI answering service. AI call center software for customer service automation. Build AI phone agents in 60 seconds.",
          operatingSystem: "Web",
          offers: {
            "@type": "AggregateOffer",
            lowPrice: "0",
            highPrice: "499",
            priceCurrency: "USD",
            offerCount: "3"
          },
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: "4.9",
            ratingCount: "500",
            bestRating: "5"
          }
        })}
        </script>

        {/* Structured Data - FAQPage */}
        <script type="application/ld+json">
          {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [{
            "@type": "Question",
            name: "What is an AI answering service and how does it work?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "An AI answering service uses artificial intelligence to handle phone calls automatically 24/7. Call24x7.AI's AI customer support platform answers calls, books appointments, processes payments, and provides customer service - just like a human receptionist but available around the clock."
            }
          }, {
            "@type": "Question",
            name: "How can AI reduce customer support costs?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "AI customer service automation can reduce support costs by up to 70%. Our AI call center software handles unlimited calls simultaneously, eliminates hold times, and works 24/7 without overtime pay. Small businesses save thousands monthly with our AI receptionist service."
            }
          }, {
            "@type": "Question",
            name: "How do I automate customer calls with AI?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "With Call24x7.AI, you can automate customer calls in 60 seconds. Simply enter your website URL or describe your needs, and our AI builds a custom phone agent. No coding required - our AI tools for small business call handling are designed for easy setup."
            }
          }, {
            "@type": "Question",
            name: "Is Call24x7.AI suitable for small businesses?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes! Our AI receptionist service is perfect for small businesses. Get professional 24/7 AI answering service starting free. Handle calls, book appointments, and provide customer support without hiring additional staff."
            }
          }]
        })}
        </script>

        {/* Structured Data - Product */}
        <script type="application/ld+json">
          {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          name: "Call24x7.AI Phone Agent",
          description: "AI customer support platform with 24/7 AI answering service, AI call center software, and AI receptionist service",
          brand: {
            "@type": "Brand",
            name: "Call24x7.AI"
          },
          offers: {
            "@type": "AggregateOffer",
            lowPrice: "0",
            highPrice: "499",
            priceCurrency: "USD",
            offerCount: "3",
            availability: "https://schema.org/InStock"
          },
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: "4.9",
            ratingCount: "500",
            bestRating: "5"
          }
        })}
        </script>

        {/* Structured Data - BreadcrumbList */}
        <script type="application/ld+json">
          {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [{
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://call24x7.ai"
          }]
        })}
        </script>
      </Helmet>
      <CookieConsent />

      {/* Breaking News Banner */}
      <RotatingNewsBanner />

      {/* Header */}
      <header className={`fixed top-10 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "bg-background/95 backdrop-blur-md shadow-md" : "bg-transparent"}`}>
        <div className="container mx-auto sm:px-4 py-[27px] px-px">
          <div className="flex items-center justify-between">
            <a href="https://www.call24x7.ai" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <img src={logo} alt="Call24x7 Logo" className="h-8 w-8" width="32" height="32" fetchPriority="high" loading="eager" />
              <span className="text-xl md:text-2xl font-bold text-primary">Call24x7</span>
            </a>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-4 xl:gap-6">
              <button onClick={() => handleSmoothScroll("features")} className="text-sm xl:text-base text-muted-foreground hover:text-primary transition-colors">
                {t("header.features")}
              </button>
              <button onClick={() => handleSmoothScroll("api")} className="text-sm xl:text-base text-muted-foreground hover:text-primary transition-colors">
                {t("header.api")}
              </button>
              <button onClick={() => handleSmoothScroll("pricing")} className="text-sm xl:text-base text-muted-foreground hover:text-primary transition-colors">
                {t("header.pricing")}
              </button>
              <Link to="/blog" className="text-sm xl:text-base text-muted-foreground hover:text-primary transition-colors">
                {t("header.blog")}
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger className="text-sm xl:text-base text-muted-foreground hover:text-primary transition-colors flex items-center gap-1">
                  {t("header.resources")} <ChevronDown className="h-4 w-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-background border border-border shadow-lg z-50">
                  <DropdownMenuItem asChild>
                    <Link to="/industries" className="cursor-pointer">
                      {t("header.industries")}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/case-studies" className="cursor-pointer">
                      {t("header.caseStudies")}
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </nav>

            {/* Desktop Actions */}
            <div className="hidden md:flex items-center gap-2 lg:gap-3">
              <LanguageSelector />
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCalendlyOpen(true)}
                className="gap-1"
              >
                <Calendar className="h-4 w-4" />
                {t("header.bookMeeting")}
              </Button>
              <ContactForm />
              <CalendlyModal open={calendlyOpen} onOpenChange={setCalendlyOpen} />
              <Button variant="ghost" size="sm" onClick={() => window.location.href = "https://manage.call24x7.ai/login"}>
                {t("header.login")}
              </Button>
              <Button size="sm" onClick={() => window.location.href = "https://manage.call24x7.ai/signup"}>
                {t("header.signUp")}
              </Button>
            </div>

            {/* Mobile Actions - Language + Signup + Menu */}
            <div className="flex md:hidden items-center gap-2">
              <LanguageSelector />
              <Button size="sm" className="text-xs px-3 h-8" onClick={() => window.location.href = "https://manage.call24x7.ai/signup"}>
                {t("header.signUp")}
              </Button>
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-10 w-10" aria-label="Open menu">
                    <Menu className="h-6 w-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[300px] bg-background">
                  <nav className="flex flex-col gap-4 mt-8">
                    <button onClick={() => {
                    handleSmoothScroll("features");
                    setMobileMenuOpen(false);
                  }} className="text-left py-3 px-4 text-base font-medium text-foreground hover:bg-muted rounded-lg transition-colors">
                      {t("header.features")}
                    </button>
                    <button onClick={() => {
                    handleSmoothScroll("api");
                    setMobileMenuOpen(false);
                  }} className="text-left py-3 px-4 text-base font-medium text-foreground hover:bg-muted rounded-lg transition-colors">
                      {t("header.api")}
                    </button>
                    <button onClick={() => {
                    handleSmoothScroll("pricing");
                    setMobileMenuOpen(false);
                  }} className="text-left py-3 px-4 text-base font-medium text-foreground hover:bg-muted rounded-lg transition-colors">
                      {t("header.pricing")}
                    </button>
                    <Link to="/blog" onClick={() => setMobileMenuOpen(false)} className="text-left py-3 px-4 text-base font-medium text-foreground hover:bg-muted rounded-lg transition-colors">
                      {t("header.blog")}
                    </Link>
                    <div className="px-4 py-2 text-sm font-semibold text-muted-foreground">{t("header.resources")}</div>
                    <Link to="/industries" onClick={() => setMobileMenuOpen(false)} className="text-left py-3 px-4 pl-8 text-base font-medium text-foreground hover:bg-muted rounded-lg transition-colors">
                      {t("header.industries")}
                    </Link>
                    <Link to="/case-studies" onClick={() => setMobileMenuOpen(false)} className="text-left py-3 px-4 pl-8 text-base font-medium text-foreground hover:bg-muted rounded-lg transition-colors">
                      {t("header.caseStudies")}
                    </Link>

                    <div className="pt-4 mt-4 border-t border-border flex flex-col gap-3">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setCalendlyOpen(true);
                          setMobileMenuOpen(false);
                        }}
                        className="w-full h-12 text-base gap-2"
                      >
                        <Calendar className="h-5 w-5" />
                        {t("header.bookMeeting")}
                      </Button>
                      <div className="px-4">
                        <ContactForm />
                      </div>
                      <Button variant="ghost" onClick={() => window.location.href = "https://manage.call24x7.ai/login"} className="w-full justify-start h-12 text-base">
                        {t("header.login")}
                      </Button>
                      <Button onClick={() => window.location.href = "https://manage.call24x7.ai/signup"} className="w-full h-12 text-base">
                        {t("header.signUp")}
                      </Button>
                    </div>
                  </nav>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main id="main-content">
      {/* Hero Section */}
      <section id="home" className="pt-32 pb-8 px-2 sm:px-4">
        <div className="container mx-auto text-center px-2 sm:px-4">
          <div className="inline-block mb-3 px-4 md:px-0">
            <a href="https://manage.call24x7.ai/signup" className="bg-gradient-to-r from-primary to-secondary p-1 rounded-2xl block hover:opacity-90 transition-opacity cursor-pointer">
              <div className="bg-card px-6 py-2 rounded-2xl">
                <p className="text-sm text-muted-foreground">{t("hero.badge")}</p>
              </div>
            </a>
          </div>

          <Link to="/url-to-agent">
            <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-4 px-4 md:px-0 cursor-pointer hover:opacity-80 transition-opacity">
              <span className="text-foreground">{t("hero.title")}</span>
              <br />
              <span className="bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
                {t("hero.titleHighlight")}
              </span>
            </h1>
          </Link>

          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto mb-6 px-4 md:px-0">{t("hero.subtitle")}</p>

          {/* Method Selection Cards */}
          {flowStep === "home" && !activeMethod && <div id="create" className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-5xl mx-auto mb-6 px-4 md:px-0">
              <Link to="/chat-to-agent" id="chat" className="group p-5 border-2 border-border hover:border-primary rounded-2xl bg-card hover:bg-card/80 transition-all hover:scale-105 text-left">
                <div className="flex flex-col items-center text-center space-y-3">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors" aria-hidden="true">
                    <MessageSquare className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-foreground mb-1">{t("methods.chat.title")}</h2>
                    <p className="text-xs text-muted-foreground">{t("methods.chat.description")}</p>
                  </div>
                </div>
              </Link>

              <Link to="/url-to-agent" id="website" className="group p-6 border-4 border-primary/60 hover:border-primary rounded-2xl bg-gradient-to-br from-card to-primary/5 hover:bg-gradient-to-br hover:from-card hover:to-primary/10 transition-all hover:scale-105 text-left shadow-2xl">
                <div className="flex flex-col items-center text-center space-y-3">
                  <div className="w-16 h-16 rounded-full bg-primary/30 flex items-center justify-center group-hover:bg-primary/40 transition-colors shadow-lg" aria-hidden="true">
                    <Globe className="w-8 h-8 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-xl font-black text-foreground mb-1">{t("methods.website.title")}</h2>
                    <p className="text-sm font-bold text-muted-foreground">{t("methods.website.description")}</p>
                  </div>
                </div>
              </Link>

              <button id="template" onClick={() => setActiveMethod("template")} className="group p-5 border-2 border-border hover:border-primary rounded-2xl bg-card hover:bg-card/80 transition-all hover:scale-105 text-left">
                <div className="flex flex-col items-center text-center space-y-3">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors" aria-hidden="true">
                    <LayoutTemplate className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-foreground mb-1">{t("methods.template.title")}</h2>
                    <p className="text-xs text-muted-foreground">{t("methods.template.description")}</p>
                  </div>
                </div>
              </button>
            </div>}

          {/* Active Method Display */}
          {flowStep === "home" && activeMethod === "chat" && <div className="max-w-5xl mx-auto animate-scale-in mb-8 px-0 md:px-4" data-active-method>
              <Button variant="ghost" onClick={() => setActiveMethod(null)} className="mb-4 text-muted-foreground hover:text-foreground">
                {t("methods.backToOptions")}
              </Button>
              <div className="border border-border rounded-2xl overflow-hidden bg-card">
                <ChatInterface initialConfig={agentConfig} onComplete={(config, messages, sid, aid) => {
                setAgentConfig(config);
                setSessionId(sid);
                setAgentId(aid);
                setFlowStep("test");
              }} onBack={() => setActiveMethod(null)} />
              </div>
            </div>}

          {flowStep === "home" && activeMethod === "url" && <div className="max-w-5xl mx-auto animate-scale-in mb-8 px-0 md:px-4" data-active-method>
              <Button variant="ghost" onClick={() => setActiveMethod(null)} className="mb-4 text-muted-foreground hover:text-foreground">
                {t("methods.backToOptions")}
              </Button>
              <div className="border border-border rounded-2xl overflow-hidden bg-card">
                <UrlAnalyzer onComplete={config => {
                setAgentConfig(config);
                setFlowStep("test");
              }} onBack={() => setActiveMethod(null)} />
              </div>
            </div>}

          {flowStep === "home" && activeMethod === "template" && <div className="max-w-5xl mx-auto animate-scale-in mb-8 px-0 md:px-4" data-active-method>
              <Button variant="ghost" onClick={() => setActiveMethod(null)} className="mb-4 text-muted-foreground hover:text-foreground">
                {t("methods.backToOptions")}
              </Button>
              <div className="border border-border rounded-2xl overflow-hidden bg-card">
                <TemplateSelector onSelect={config => {
                setAgentConfig(config);
                setFlowStep("test");
              }} onBack={() => setActiveMethod(null)} onCustom={() => setActiveMethod("chat")} />
              </div>
            </div>}

          <div className="flex flex-col sm:flex-row gap-4 justify-center px-4 md:px-0">
            {!activeMethod && <>
               <Button size="lg" onClick={() => window.location.href = 'https://manage.call24x7.ai/signup'}>
                  {t("hero.getStarted")}
                </Button>
                <Button size="lg" variant="outline" onClick={() => handleSmoothScroll("features")}>
                  {t("hero.exploreFeatures")}
                </Button>
              </>}
          </div>
        </div>
      </section>

      {/* Test Options Step */}
      {flowStep === "test" && <section className="py-20 px-0 md:px-4">
          <div className="container mx-auto px-0 md:px-4">
            <TestOptions agentConfig={agentConfig} sessionId={sessionId} agentId={agentId} onRegister={(sid, aid) => {
            setSessionId(sid);
            setAgentId(aid);
            setFlowStep("register");
          }} onBack={() => {
            setFlowStep("home");
            setActiveMethod(null);
          }} />
          </div>
        </section>}

      {/* Registration Step */}
      {flowStep === "register" && <section className="py-20 px-0 md:px-4">
          <div className="container mx-auto px-0 md:px-4">
            <RegistrationPrompt agentConfig={agentConfig} sessionId={sessionId} agentId={agentId} onComplete={() => {
            alert("Registration complete! Your agent has been saved.");
            setFlowStep("home");
            setActiveMethod(null);
          }} />
          </div>
        </section>}

      {/* Only show sections below when on home step */}
      {flowStep === "home" && <>
          {/* Statistics Counter Section */}
          <section className="py-16 px-4 bg-gradient-to-r from-primary/10 via-secondary/10 to-primary/10">
            <div className="container mx-auto">
              <div className="grid md:grid-cols-4 gap-8 text-center">
                <div className="space-y-2">
                  <div className="text-5xl font-bold text-primary">50K+</div>
                  <div className="text-muted-foreground">{t("stats.callsHandled")}</div>
                  <div className="text-sm text-primary">{t("stats.callsIncrease")}</div>
                </div>
                <div className="space-y-2">
                  <div className="text-5xl font-bold text-primary">99.9%</div>
                  <div className="text-muted-foreground">{t("stats.uptime")}</div>
                  <div className="text-sm text-primary">{t("stats.availability")}</div>
                </div>
                <div className="space-y-2">
                  <div className="text-5xl font-bold text-primary">230M+</div>
                  <div className="text-muted-foreground">{t("stats.totalCalls")}</div>
                  <div className="text-sm text-primary">{t("stats.growing")}</div>
                </div>
                <div className="space-y-2">
                  <div className="text-5xl font-bold text-primary">4.9/5</div>
                  <div className="text-muted-foreground">{t("stats.rating")}</div>
                  <div className="text-sm text-primary">{t("stats.reviews")}</div>
                </div>
              </div>
            </div>
            
            {/* Live Activity Ticker */}
            <div className="mt-8">
              <LiveActivityTicker />
            </div>
          </section>

          {/* Call Recording Demo Section - Hidden for now */}
          {false && <section className="py-16 px-4">
              <div className="container mx-auto">
                <div className="text-center mb-10">
                  <Badge variant="secondary" className="mb-4">Call Recording</Badge>
                  <h2 className="text-3xl md:text-4xl font-bold mb-4">
                    Listen to Every Conversation
                  </h2>
                  <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                    Review AI agent calls with full recordings, live transcripts, and speaker detection
                  </p>
                </div>
                <div className="max-w-2xl mx-auto">
                  <CallRecordingDemo />
                </div>
              </div>
            </section>}

          {/* Trusted By Section */}
          <section className="py-16 px-4">
            <div className="container mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-2">{t("trusted.title")}</h2>
                <p className="text-muted-foreground">{t("trusted.subtitle")}</p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center justify-items-center">
                <div className="text-2xl font-bold text-foreground/70">{t("trusted.healthcare")}</div>
                <div className="text-2xl font-bold text-foreground/70">{t("trusted.ecommerce")}</div>
                <div className="text-2xl font-bold text-foreground/70">{t("trusted.finance")}</div>
                <div className="text-2xl font-bold text-foreground/70">{t("trusted.realEstate")}</div>
                <div className="text-2xl font-bold text-foreground/70">{t("trusted.legal")}</div>
                <div className="text-2xl font-bold text-foreground/70">{t("trusted.education")}</div>
                <div className="text-2xl font-bold text-foreground/70">{t("trusted.hospitality")}</div>
                <div className="text-2xl font-bold text-foreground/70">{t("trusted.insurance")}</div>
              </div>
            </div>
          </section>

          {/* Featured In Press Section */}
          <section className="py-12 px-2 sm:px-4 border-t border-border/30">
            <div className="container mx-auto">
              <div className="text-center mb-8">
                <Badge variant="outline" className="mb-4">
                  <FileText className="w-3 h-3 mr-1" />
                  Press Release
                </Badge>
                <h2 className="text-2xl md:text-3xl font-bold mb-2">Featured In</h2>
                <p className="text-muted-foreground">Latest news and announcements</p>
              </div>
              
              <div className="max-w-2xl mx-auto">
                <a href="https://digitaljournal.com/pr/news/pr-zen/call24x7-ai-launches-next-generation-ai-phone-1932262898.html" target="_blank" rel="noopener noreferrer" className="group block">
                  <Card className="overflow-hidden hover:shadow-lg transition-all duration-300 border-border/50 hover:border-primary/30">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                          <div className="w-16 h-16 md:w-20 md:h-20 bg-[#c41200] rounded-lg flex items-center justify-center">
                            <span className="text-white font-bold text-xs md:text-sm text-center leading-tight px-1">DIGITAL<br />JOURNAL</span>
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="secondary" className="text-xs">Press Release</Badge>
                            <span className="text-xs text-muted-foreground">December 9, 2025</span>
                          </div>
                          <h3 className="text-lg md:text-xl font-semibold text-foreground group-hover:text-primary transition-colors mb-2 line-clamp-2">
                            Call24x7.AI Launches Next-Generation AI Phone Agent
                          </h3>
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            New AI-powered phone agent helps businesses automate calls, boost customer experience, and reduce support costs.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </a>
              </div>
            </div>
          </section>

          {/* Featured Blog Posts Section */}
          <FeaturedBlogPosts />

          {/* Features Section */}
          <section id="features" className="py-20 px-2 sm:px-4">
            <div className="container mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-4xl font-bold mb-4">{t("features.title")}</h2>
                <p className="text-muted-foreground text-lg">{t("features.subtitle")}</p>
              </div>

              {selectedFeature === null ? <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 animate-fade-in">
                  {[{
                icon: PhoneCall,
                key: "inboundOutbound"
              }, {
                icon: Calendar,
                key: "appointments"
              }, {
                icon: TrendingUp,
                key: "payment"
              }, {
                icon: Headphones,
                key: "transfer"
              }, {
                icon: Clock,
                key: "availability"
              }, {
                icon: Globe,
                key: "multiLanguage"
              }, {
                icon: Mic,
                key: "naturalVoice"
              }, {
                icon: Building2,
                key: "crm"
              }, {
                icon: BarChart,
                key: "analytics"
              }, {
                icon: Shield,
                key: "security"
              }, {
                icon: Workflow,
                key: "noCode"
              }, {
                icon: Radio,
                key: "recording"
              }, {
                icon: Webhook,
                key: "mcpServer"
              }, {
                icon: Waves,
                key: "voiceCloning"
              }, {
                icon: PhoneIncoming,
                key: "phoneNumber"
              }].map((feature, index) => <Card key={index} className="hover:shadow-lg transition-all cursor-pointer hover:scale-105" onClick={() => setSelectedFeature(index)}>
                      <CardHeader>
                        <feature.icon className="h-10 w-10 text-primary mb-2" />
                        <CardTitle className="text-lg">{t(`features.cards.${feature.key}.title`)}</CardTitle>
                        <CardDescription>{t(`features.cards.${feature.key}.desc`)}</CardDescription>
                      </CardHeader>
                    </Card>)}
                </div> : <div className="max-w-4xl mx-auto animate-scale-in">
                  <Button variant="ghost" onClick={() => setSelectedFeature(null)} className="mb-6 text-muted-foreground hover:text-foreground">
                    {t("features.backToAll")}
                  </Button>
                  <Card className="p-8">
                    {(() => {
                  const featureKeys = ["inboundOutbound", "appointments", "payment", "transfer", "availability", "multiLanguage", "naturalVoice", "crm", "analytics", "security", "noCode", "recording", "mcpServer", "voiceCloning", "phoneNumber"];
                  const icons = [PhoneCall, Calendar, TrendingUp, Headphones, Clock, Globe, Mic, Building2, BarChart, Shield, Workflow, Radio, Webhook, Waves, PhoneIncoming];
                  const featureKey = featureKeys[selectedFeature];
                  const FeatureIcon = icons[selectedFeature];
                  return <>
                          <div className="flex items-start gap-6 mb-6">
                            <div className="p-4 rounded-full bg-primary/10">
                              <FeatureIcon className="h-12 w-12 text-primary" />
                            </div>
                            <div className="flex-1">
                              <h3 className="text-3xl font-bold mb-2">{t(`features.cards.${featureKey}.title`)}</h3>
                              <p className="text-xl text-muted-foreground">{t(`features.cards.${featureKey}.desc`)}</p>
                            </div>
                          </div>
                          <div className="prose prose-lg max-w-none">
                            <p className="text-foreground leading-relaxed">
                              {t(`features.cards.${featureKey}.details`)}
                            </p>
                          </div>
                          <div className="mt-8 flex gap-4">
                            <Button size="lg" onClick={() => window.location.href = "https://manage.call24x7.ai/signup"}>
                              {t("hero.getStarted")}
                            </Button>
                            <Button size="lg" variant="outline" onClick={() => handleSmoothScroll("pricing")}>
                              {t("features.viewPricing")}
                            </Button>
                          </div>
                        </>;
                })()}
                  </Card>
                </div>}
            </div>
          </section>

          {/* Capabilities Section */}
          <section id="capabilities" className="py-20 px-2 sm:px-4 bg-muted/30">
            <div className="container mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-4xl font-bold mb-4">{t("capabilities.title")}</h2>
                <p className="text-muted-foreground text-lg">{t("capabilities.subtitle")}</p>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  {["Handle complex multi-turn conversations", "Book and reschedule appointments", "Process payments and orders", "Provide technical support", "Qualify leads and schedule demos", "Send follow-up emails and SMS"].map((capability, index) => <div key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                      <p className="text-lg">{capability}</p>
                    </div>)}
                </div>

                <div className="space-y-4">
                  {["Answer FAQs with custom knowledge base", "Transfer to human agents when needed", "Collect customer feedback and surveys", "Update CRM records automatically", "Handle objections and negotiate", "Provide order status and tracking"].map((capability, index) => <div key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                      <p className="text-lg">{capability}</p>
                    </div>)}
                </div>
              </div>

              {/* SEO-optimized content with buyer-intent keywords */}
              <div className="mt-12 text-center">
                <p className="text-sm text-muted-foreground max-w-4xl mx-auto leading-relaxed">
                  Our <strong>AI customer support platform</strong> provides comprehensive <strong>AI customer service automation</strong> for businesses of all sizes. 
                  Whether you need a <strong>24/7 AI answering service</strong> or advanced <strong>AI call center software</strong>, Call24x7 delivers enterprise-grade 
                  <strong> AI receptionist service</strong> that transforms how you handle customer communications.
                </p>
              </div>
            </div>
          </section>

          {/* Why Choose AI Section - SEO Rich */}
          <section className="py-16 px-2 sm:px-4">
            <div className="container mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-3xl font-bold mb-4">Why Businesses Choose AI Customer Support</h2>
                <p className="text-muted-foreground max-w-2xl mx-auto">
                  Learn how to automate customer calls with AI and reduce support costs by up to 70%
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                <Card className="p-6 text-center hover:shadow-lg transition-shadow">
                  <div className="text-4xl font-bold text-primary mb-2">70%</div>
                  <h3 className="font-semibold mb-2">Cost Reduction</h3>
                  <p className="text-sm text-muted-foreground">
                    AI tools for small business call handling reduce operational costs dramatically
                  </p>
                </Card>
                <Card className="p-6 text-center hover:shadow-lg transition-shadow">
                  <div className="text-4xl font-bold text-primary mb-2">24/7</div>
                  <h3 className="font-semibold mb-2">Always Available</h3>
                  <p className="text-sm text-muted-foreground">
                    AI answering service handles calls around the clock, never misses a customer
                  </p>
                </Card>
                <Card className="p-6 text-center hover:shadow-lg transition-shadow">
                  <div className="text-4xl font-bold text-primary mb-2">60s</div>
                  <h3 className="font-semibold mb-2">Quick Setup</h3>
                  <p className="text-sm text-muted-foreground">
                    Deploy AI receptionist service in under a minute, no coding required
                  </p>
                </Card>
              </div>

              <div className="mt-10 text-center">
                <p className="text-sm text-muted-foreground max-w-3xl mx-auto">
                  Wondering <strong>how to automate customer calls with AI</strong>? Our platform makes it simple. 
                  From <strong>AI tools for small business call handling</strong> to enterprise-grade <strong>AI call center software</strong>, 
                  we provide the complete solution for modern customer communication.
                </p>
              </div>
            </div>
          </section>

          {/* Industries Section */}
          <section id="industries" className="py-20 px-2 sm:px-4 bg-muted/30">
            <div className="container mx-auto">
              <div className="text-center mb-12">
                <Badge variant="secondary" className="mb-4">50+ Industries Served</Badge>
                <h2 className="text-4xl font-bold mb-4">AI Phone Agents for Every Industry</h2>
                <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                  From healthcare to hospitality, our AI phone agents are transforming customer communication across industries.
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                {[
                  { name: "Medical Clinics", icon: "Stethoscope", id: "medical-clinics", gradient: "from-emerald-500/20 to-teal-500/20" },
                  { name: "Real Estate", icon: "Home", id: "real-estate-agencies", gradient: "from-blue-500/20 to-cyan-500/20" },
                  { name: "Restaurants", icon: "Utensils", id: "restaurants-dining", gradient: "from-orange-500/20 to-red-500/20" },
                  { name: "Auto Dealerships", icon: "Car", id: "auto-dealerships", gradient: "from-blue-500/20 to-indigo-500/20" },
                  { name: "Law Firms", icon: "Scale", id: "law-firms", gradient: "from-amber-600/20 to-yellow-600/20" },
                  { name: "Hotels", icon: "Building2", id: "hotels-resorts", gradient: "from-amber-500/20 to-orange-500/20" },
                  { name: "E-Commerce", icon: "ShoppingCart", id: "ecommerce-stores", gradient: "from-purple-500/20 to-pink-500/20" },
                  { name: "HVAC Services", icon: "Zap", id: "hvac-services", gradient: "from-sky-500/20 to-blue-500/20" },
                ].map((industry) => (
                  <Link key={industry.id} to={`/industries/${industry.id}`}>
                    <Card className={`h-full hover:shadow-lg transition-all hover:scale-[1.02] bg-gradient-to-br ${industry.gradient} border-border/50`}>
                      <CardContent className="p-4 text-center">
                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-3">
                          {industry.icon === "Stethoscope" && <Stethoscope className="h-6 w-6 text-primary" />}
                          {industry.icon === "Home" && <Home className="h-6 w-6 text-primary" />}
                          {industry.icon === "Utensils" && <Utensils className="h-6 w-6 text-primary" />}
                          {industry.icon === "Car" && <Car className="h-6 w-6 text-primary" />}
                          {industry.icon === "Scale" && <Scale className="h-6 w-6 text-primary" />}
                          {industry.icon === "Building2" && <Building2 className="h-6 w-6 text-primary" />}
                          {industry.icon === "ShoppingCart" && <ShoppingCart className="h-6 w-6 text-primary" />}
                          {industry.icon === "Zap" && <Zap className="h-6 w-6 text-primary" />}
                        </div>
                        <h3 className="font-semibold text-foreground">{industry.name}</h3>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>

              <div className="text-center">
                <Button variant="outline" size="lg" asChild>
                  <Link to="/industries">
                    View All 50+ Industries <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </section>

          {/* API Section */}
          <section id="api" className="py-20 px-2 sm:px-4">
            <div className="container mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-4xl font-bold mb-4">{t("api.title")}</h2>
                <p className="text-muted-foreground text-lg max-w-2xl mx-auto">{t("api.subtitle")}</p>
              </div>

              <div className="grid lg:grid-cols-3 gap-8">
                <Card className="hover:border-primary/50 transition-colors">
                  <a href="https://doc.call24x7.ai/essentials/getting-started" target="_blank" rel="noopener noreferrer" className="block h-full">
                    <CardHeader>
                      <Zap className="h-10 w-10 text-primary mb-2" />
                      <CardTitle>No-Code Integration</CardTitle>
                      <CardDescription>Build without writing a single line of code</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        <li className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-primary" />
                          <span>Zapier & Make.com workflows</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-primary" />
                          <span>n8n automation</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-primary" />
                          <span>MCP server integration</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-primary" />
                          <span>Visual drag-and-drop builders</span>
                        </li>
                      </ul>
                    </CardContent>
                  </a>
                </Card>

                <Card className="hover:border-primary/50 transition-colors">
                  <a href="https://doc.call24x7.ai/essentials/getting-started" target="_blank" rel="noopener noreferrer" className="block h-full">
                    <CardHeader>
                      <Code className="h-10 w-10 text-primary mb-2" />
                      <CardTitle>Vibe Coding</CardTitle>
                      <CardDescription>AI-powered development for rapid prototyping</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        <li className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-primary" />
                          <span>Simple REST API endpoints</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-primary" />
                          <span>Copy-paste code examples</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-primary" />
                          <span>AI assistant friendly docs</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-primary" />
                          <span>Quick integration tutorials</span>
                        </li>
                      </ul>
                    </CardContent>
                  </a>
                </Card>

                <Card className="hover:border-primary/50 transition-colors">
                  <a href="https://doc.call24x7.ai/essentials/getting-started" target="_blank" rel="noopener noreferrer" className="block h-full">
                    <CardHeader>
                      <FileText className="h-10 w-10 text-primary mb-2" />
                      <CardTitle>Full Documentation</CardTitle>
                      <CardDescription>Complete guides and API reference</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        <li className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-primary" />
                          <span>Getting started guides</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-primary" />
                          <span>Webhook & API reference</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-primary" />
                          <span>SDK for Python, Node.js & more</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-primary" />
                          <span>Best practices and tips</span>
                        </li>
                      </ul>
                    </CardContent>
                  </a>
                </Card>
              </div>

              <div className="mt-8 p-6 bg-muted rounded-lg">
                <code className="text-sm block overflow-x-auto">
                  <span className="text-primary">// Example: Initiate an outbound call</span>
                  <br />
                  <span className="text-foreground/80">const</span> response ={" "}
                  <span className="text-foreground/80">await</span> call24x7.calls.create({"{"}
                  <br />
                  &nbsp;&nbsp;to: <span className="text-emerald-700 dark:text-emerald-300">"+1234567890"</span>,<br />
                  &nbsp;&nbsp;agentId: <span className="text-emerald-700 dark:text-emerald-300">"agent_abc123"</span>,<br />
                  &nbsp;&nbsp;context: {"{"} campaign: <span className="text-emerald-700 dark:text-emerald-300">"summer-sale"</span> {"}"}
                  <br />
                  {"}"});
                </code>
              </div>
            </div>
          </section>

          {/* Tech Partners */}
          

          {/* Pricing */}
          <section id="pricing">
            <PricingSection />
          </section>

          {/* CTA Section */}
          <section className="py-20 px-2 sm:px-4 bg-gradient-to-r from-primary/10 to-primary/5">
            <div className="container mx-auto text-center">
              <h2 className="text-4xl font-bold mb-4">{t("cta.title")}</h2>
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">{t("cta.subtitle")}</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" onClick={() => window.location.href = "https://manage.call24x7.ai/signup"}>
                  {t("cta.button")}
                </Button>
              </div>
            </div>
          </section>

          {/* Footer */}
          <footer className="bg-muted/50 py-12 px-2 sm:px-4">
            <div className="container mx-auto">
              <div className="grid md:grid-cols-4 gap-8 mb-8">
                {/* Company */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <img src={logo} alt="Call24x7 Logo" className="h-6 w-6" />
                    <span className="text-xl font-bold">Call24x7</span>
                  </div>
                  <p className="text-muted-foreground text-sm mb-4">{t("footer.tagline")}</p>
                  <div className="flex gap-3">
                    <a href="https://www.facebook.com/p/Call24x7AI-61567709152635/" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors" aria-label="Follow us on Facebook">
                      <Facebook className="h-5 w-5" />
                    </a>
                    <a href="https://www.instagram.com/call24x7.ai/" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors" aria-label="Follow us on Instagram">
                      <Instagram className="h-5 w-5" />
                    </a>
                    <a href="https://www.youtube.com/channel/UCLLWm2gsbHx7a_rt5oLSGkA" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors" aria-label="Subscribe to our YouTube channel">
                      <Youtube className="h-5 w-5" />
                    </a>
                  </div>
                </div>

                {/* Product */}
                <div>
                  <h3 className="font-semibold mb-4">{t("footer.product")}</h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>
                      <a href="#features" className="hover:text-primary transition-colors">
                        {t("footer.features")}
                      </a>
                    </li>
                    <li>
                      <a href="#pricing" className="hover:text-primary transition-colors">
                        {t("footer.pricing")}
                      </a>
                    </li>
                    <li>
                      <Link to="/#api" className="hover:text-primary transition-colors">
                        {t("footer.api")}
                      </Link>
                    </li>
                    <li>
                      <Link to="/#api" className="hover:text-primary transition-colors">
                        Docs
                      </Link>
                    </li>
                    <li>
                      <a href="#" className="hover:text-primary transition-colors">
                        {t("footer.integrations")}
                      </a>
                    </li>
                    <li>
                      <a href="#" className="hover:text-primary transition-colors">
                        Changelog
                      </a>
                    </li>
                  </ul>
                </div>

                {/* Company */}
                <div>
                  <h3 className="font-semibold mb-4">{t("footer.company")}</h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>
                      <a href="#" className="hover:text-primary transition-colors">
                        {t("footer.about")}
                      </a>
                    </li>
                    <li>
                      <a href="#" className="hover:text-primary transition-colors">
                        {t("footer.careers")}
                      </a>
                    </li>
                    <li>
                      <Link to="/blog" className="hover:text-primary transition-colors">
                        {t("footer.blog")}
                      </Link>
                    </li>
                    <li>
                      <Link to="/referral" className="hover:text-primary transition-colors">
                        Referral Program
                      </Link>
                    </li>
                    <li>
                      <a href="#" className="hover:text-primary transition-colors">
                        Press Kit
                      </a>
                    </li>
                    <li>
                      <a href="#" className="hover:text-primary transition-colors">
                        {t("footer.contact")}
                      </a>
                    </li>
                  </ul>
                </div>

                {/* Legal */}
                <div>
                  <h3 className="font-semibold mb-4">{t("footer.legal")}</h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>
                      <Link to="/terms-of-use" className="hover:text-primary transition-colors">
                        {t("footer.terms")}
                      </Link>
                    </li>
                    <li>
                      <Link to="/privacy-policy" className="hover:text-primary transition-colors">
                        {t("footer.privacy")}
                      </Link>
                    </li>
                    <li>
                      <a href="#" className="hover:text-primary transition-colors">
                        Cookie Policy
                      </a>
                    </li>
                    <li>
                      <a href="#" className="hover:text-primary transition-colors">
                        {t("footer.hipaa")}
                      </a>
                    </li>
                    <li>
                      <a href="#" className="hover:text-primary transition-colors">
                        {t("footer.gdpr")}
                      </a>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="border-t border-border pt-8">
                <SocialShareButtons className="mb-6" />
                <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Shield className="h-4 w-4 text-primary" />
                      <span>{t("footer.hipaa")}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Lock className="h-4 w-4 text-primary" />
                      <span>{t("footer.compliance")}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    <span>{t("footer.location")}</span>
                  </div>
                  <p>© 2024 Call24x7. {t("footer.rights")}</p>
                </div>
              </div>
            </div>
          </footer>
        </>}
      </main>
    </div>;
};
export default Index;